import { Trans } from 'react-i18next';
import { Header } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import { WeezHeader } from '@weezevent/nacre';
import $ from 'jquery';
import DataTable from 'datatables.net';
import useAppContext from '../../context/useAppContext';

import { urlPrefix } from './quotaSummary';

$.DataTable = DataTable;

function Headers() {
    return (
        <React.Fragment>
            <th key="baseRatesTitle" className="tabular-quota-header">
                <span>
                    <Trans i18nKey="ratesZonesTitle">Rates / Zones</Trans>
                </span>
            </th>

            <th className="tabular-quota-header">
                <span>
                    <Trans i18nKey="ticket.chart.zone">Zone</Trans>
                </span>
            </th>

            <th className="tabular-quota-header">
                <span>
                    <Trans i18nKey="ticket.chart.capacity">Capacity</Trans>
                </span>
            </th>

            <th className="tabular-quota-header">
                <span>
                    <Trans i18nKey="ticket.common.action.edit">Modify</Trans>
                </span>
            </th>
        </React.Fragment>
    );
}

export default function QuotasTabularEg({ quotasSummary, generalAdmissionId, quotas, rates, zones, currentInventory, renderInventorySelector }) {
    const [{ event, organization }] = useAppContext();
    let organizationId = organization.id;
    let eventId = event.id;

    /**
     * Filter quotas to get only the quota we want to display + get properties for rates and zones
     * @returns {Array} Filtered quotas
     */
    const filterQuotas = React.useCallback(() => {
        const generalAdmission = quotasSummary.find(summary => summary.id === generalAdmissionId);
        return quotas.filter(quota => {
            if (!quota) {
                return false;
            }

            if (quota.contingent === null && quota.zone === generalAdmission.zone) {
                quota.rates_list = rates.filter(function(rate) {
                    return quota.rates.indexOf(rate.id) !== -1;
                });

                quota.zones_list = zones.filter(function(zone) {
                    return quota.zone.indexOf(zone.pk) !== -1;
                });

                return true;
            }

            return false;
        });
    }, [quotas, zones, rates]);

    /**
     * Render a rates list separated by carriage return
     * @param rates
     * @returns {string} Rates names
     */
    const getRateName = rates => {
        return rates.map(rate => rate.name).join('\n-\n');
    };

    /**
     * Render a zones list separated by carriage return
     * @param zones
     * @returns {string} Zones names
     */
    const getZonesList = zones => {
        return zones.map(zone => zone.name).join('\n-\n');
    };

    /**
     * Generate a modify button to link to the quota viewer for this GA
     * @param {Rate[]} rates - Rates to which this quota applies
     */
    const getModifyButton = React.useCallback(
        rates => {
            let selectedRates = rates.join(',');
            return (
                <td className="tabular-quota-total">
                    <Link
                        to={`${urlPrefix(
                            eventId,
                            organizationId
                        )}/seating/quotas/inventory/${currentInventory.id()}/generaladmission/${generalAdmissionId}/edit?selectedRates=${selectedRates}&editMode=true`}
                    >
                        <Trans i18nKey="ticket.common.action.edit">Modify</Trans>
                    </Link>
                </td>
            );
        },
        [rates]
    );

    /**
     * Renders quota stats in a span (in format 'booked (booking) / total')
     * @param {number} booked - Booked seats for this quota
     * @param {number} booking - Booking in progress seats for this quota
     * @param {number} total - Total seats for this quota
     * @returns {React.Component} Rendered stats
     */
    const renderQuotaStats = React.useCallback((booked, booking, total) => {
        return (
            <React.Fragment>
                <span className="tabular-text-booking" key="tabular-text-booking">
                    {booked}
                </span>
                {booking ? `(+${booking})` : null}
                {` / ${total}`}
            </React.Fragment>
        );
    }, []);

    /**
     * Generate rows for the matrix
     * @returns {Array[]} Array of arrays (matrix)
     */
    const getRows = React.useCallback(() => {
        let rows = [];
        let quotas = filterQuotas();

        quotas.forEach(quota => {
            let currentRow = [
                <td key={quota} className="tabular-quota-rate-name">
                    {getRateName(quota.rates_list)}
                </td>
            ];

            currentRow.push(<td className="tabular-quota-rate-name">{getZonesList(quota.zones_list)}</td>);

            currentRow.push(<td className="tabular-quota-rate-name">{renderQuotaStats(quota.booked, quota.bookingInProgress, quota.total)}</td>);

            currentRow.push(getModifyButton(quota.rates));

            rows.push(currentRow);
        });
        return rows;
    }, [quotas, getRateName, getZonesList, renderQuotaStats, getModifyButton]);

    /**
     * Renders Matrix Rows
     * @returns {React.Component[]} Rows
     */
    const renderRows = React.useCallback(() => {
        let rows = getRows();
        return rows.map((row, index) => <tr key={`tabularRow-${index}`}>{row}</tr>);
    }, [getRows]);

    return (
        <div>
            <div className="header">
                <WeezHeader
                    title={
                        <>
                            <Trans i18nKey="generalAdmissionQuotaTableTitle">Quotas table for general admission</Trans>
                            <Link
                                className="new-quota-button"
                                to={`${urlPrefix(eventId, organizationId)}/seating/quotas/inventory/${currentInventory.id()}/generaladmission/${generalAdmissionId}/edit`}
                            >
                                <Trans i18nKey="newQuotaButton">+ New Quota</Trans>
                            </Link>
                        </>
                    }
                />
                {renderInventorySelector()}
            </div>
            <Header as="h4">
                <Link to={`${urlPrefix(eventId, organizationId)}/seating/quotas/inventory/${currentInventory.id()}`}>
                    <Trans i18nKey="quotasList">Quotas list</Trans>
                </Link>
            </Header>
            <table id="quotas-tabular">
                <thead>
                    <tr>
                        <Headers />
                    </tr>
                </thead>

                <tbody>{renderRows()}</tbody>
            </table>
        </div>
    );
}

QuotasTabularEg.propTypes = {
    generalAdmissionId: PropTypes.number,
    currentInventory: PropTypes.object.isRequired,
    quotas: PropTypes.array,
    quotasSummary: PropTypes.array.isRequired,
    rates: PropTypes.array.isRequired,
    renderInventorySelector: PropTypes.func.isRequired,
    zones: PropTypes.array.isRequired
};
